// API для работы с заказами (интегрировано с основным приложением)

// Универсальная функция для безопасного парсинга JSON ответов
async function safeJsonParse(response: Response): Promise<any> {
  // Проверяем Content-Type ответа
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    throw new Error(`Ожидался JSON ответ, получен: ${contentType}. Ответ: ${text.substring(0, 200)}...`);
  }

  // Безопасный парсинг JSON
  const text = await response.text();
  if (!text.trim()) {
    throw new Error('Получен пустой ответ от сервера');
  }

  try {
    return JSON.parse(text);
  } catch (parseError: any) {
    throw new Error(`Ошибка парсинга JSON: ${parseError.message}. Ответ: ${text.substring(0, 200)}...`);
  }
}

const ORDERS_CONFIG = {
  baseUrl: '/api',
  endpoints: {
    orders: '/orders',
    createOrder: '/restaurant/orders',
    lastDeliveryAddress: '/last-delivery-address',
    calculateDelivery: '/calculate-delivery'
  },
  // Данные авторизации загружаются с сервера
  auth: {
    client_id: '9b876562-5706-4cd8-8a87-9dbaf69d8776',
    client_secret: '58a80a80-6639-4377-97d3-d932ec16fd9e',
    grant_type: 'client_credentials',
    scopes: 'order'
  },
  // r_keeper API для получения заказов
  rkeeperApi: {
    baseUrl: 'https://delivery.ucs.ru/orders/api/v1',
    endpoints: {
      orders: '/orders'
    }
  }
};

export interface CartItem {
  id: string;
  name: string;
  price: number;
  discount?: number;
  quantity: number;
  schemeId?: string;
  description?: string;
  imageUrls?: string[];
  measure?: { value: number; unit: string };
  isContainInStopList?: any[];
  ingredients?: any[];
  isListStoped?: boolean;
  modifiers?: { id: string; name: string; price: number; count: number }[];
  cartKey?: string;
}

export interface DeliveryAddress {
  street: string;
  building: string;
  apartment?: string;
  entrance?: string;
  floor?: string;
  comment?: string;
}

export interface OrderData {
  cartItems: CartItem[];
  deliveryAddress?: DeliveryAddress;
  address?: any;
  phone?: string;
  expectedAt?: string;
  expeditionType?: string;
  paymentType?: string;
  paymentMethod?: string;
  deliveryTime?: string;
  comment?: string;
  useBonuses?: number;
  useBonusPayment?: boolean;
  bonusAmount?: number;
  userInfo?: {
    firstName: string;
    lastName: string;
  };
}

// Утилиты для генерации данных заказа
export const OrderUtils = {
  generateOrderId(): string {
    // Генерируем ID в формате времени + случайные символы
    const timestamp = Date.now();
    const randomPart = Math.random().toString(36).substr(2, 9);
    return `${timestamp}_${randomPart}`;
  },

  formatDateTime(date: Date): string {
    return date.toISOString();
  },

  calculateTotal(cartItems: CartItem[]): number {
    return cartItems.reduce((total, item) => {
      const price = item.price || 0;
      const discount = item.discount || 0;
      const quantity = item.quantity || 1;
      const finalPrice = price - discount;
      return total + (finalPrice * quantity);
    }, 0);
  },

  formatDishList(cartItems: CartItem[]): CartItem[] {
    return cartItems.map(item => ({
      id: item.id,
      name: item.name,
      price: item.price || 0,
      discount: item.discount || 0,
      schemeId: item.schemeId || "",
      description: item.description || "",
      imageUrls: item.imageUrls || [],
      measure: item.measure || { value: 0, unit: "" },
      isContainInStopList: item.isContainInStopList || [],
      quantity: item.quantity || 1,
      ingredients: item.ingredients || [],
      isListStoped: item.isListStoped || false,
      modifiers: item.modifiers || []
    }));
  }
};

// Получить список заказов (используем данные пользователя из основного приложения)
export async function getOrders(): Promise<any> {
  try {

    // Получаем данные пользователя из основного приложения
    // TODO: интегрировать с основной системой авторизации
    // const user = JSON.parse(localStorage.getItem('user') || '{}');

    const url = `${ORDERS_CONFIG.baseUrl}${ORDERS_CONFIG.endpoints.orders}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // TODO: использовать токен из основного приложения
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      }
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(`Ошибка получения заказов: ${errorData.message || 'Неизвестная ошибка'}`);
    }

    return await safeJsonParse(response);
  } catch (error: any) {
    throw error;
  }
}

// Создать доставочный заказ
export async function createDeliveryOrder(orderData: OrderData): Promise<any> {
  try {
    console.log('📦 createDeliveryOrder called with:', orderData);

    // Используем переданные актуальные данные пользователя из Universe API
    const userInfo = orderData.userInfo || {};

    // Format order for our server (server will forward to r_keeper)
    // Server will find user by phone, so we only need to pass phone
    const serverOrder = {
      userPhone: orderData.phone,
      dishes: OrderUtils.formatDishList(orderData.cartItems),
      deliveryType: 'pickup',
      deliveryTime: orderData.expectedAt || orderData.deliveryTime,
      paymentMethod: orderData.paymentType || orderData.paymentMethod,
      comment: orderData.comment,
      useBonusPayment: orderData.useBonusPayment,
      bonusAmount: orderData.bonusAmount || 0
    };

    console.log('📦 Formatted server order:', serverOrder);

    // Send to our server proxy (which forwards to r_keeper API)
    const url = `${ORDERS_CONFIG.baseUrl}${ORDERS_CONFIG.endpoints.createOrder}`;
    console.log('📦 Sending request to URL:', url);
    console.log('📦 Full URL breakdown:', {
      baseUrl: ORDERS_CONFIG.baseUrl,
      endpoint: ORDERS_CONFIG.endpoints.createOrder,
      fullUrl: url
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify(serverOrder)
    });

    console.log('📦 Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(`Ошибка создания заказа: ${errorData.error || errorData.message || 'Неизвестная ошибка'}`);
    }

    const result = await safeJsonParse(response);
    console.log('📦 Order created successfully:', result);
    return {
      success: true,
      orderId: result.orderId,
      status: 'created',
      message: 'Заказ успешно создан в r_keeper',
      rkeeperOrderId: result.rkeeperOrderId,
      orderData: serverOrder
    };

  } catch (error: any) {
    console.error('❌ createDeliveryOrder error:', error);
    console.error('❌ Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    throw error;
  }
}

// Получить последний адрес доставки
export async function getLastDeliveryAddress(): Promise<DeliveryAddress | null> {
  try {

    // ВРЕМЕННАЯ ЗАГЛУШКА: Возвращаем null (адрес не найден)
    return null;

    /* ОРИГИНАЛЬНЫЙ КОД
    const response = await fetch(`${ORDERS_CONFIG.baseUrl}${ORDERS_CONFIG.endpoints.lastDeliveryAddress}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      }
    });

    if (!response.ok) {
      return null;
    }

    return await safeJsonParse(response);
    */
  } catch (error: any) {
    return null;
  }
}

// Рассчитать стоимость доставки
export async function calculateDelivery(address: DeliveryAddress): Promise<any> {
  try {

    // ВРЕМЕННАЯ ЗАГЛУШКА: Возвращаем фиксированную стоимость доставки

    // Имитация задержки сети
    await new Promise(resolve => setTimeout(resolve, 500));

    const mockDeliveryResult = {
      success: true,
      cost: 200, // 200 рублей за доставку
      distance: '3.5 км',
      estimatedTime: '30-45 минут',
      zones: ['center', 'delivery_available']
    };

    return mockDeliveryResult;

    /* ОРИГИНАЛЬНЫЙ КОД
    const response = await fetch(`${ORDERS_CONFIG.baseUrl}${ORDERS_CONFIG.endpoints.calculateDelivery}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify({ address })
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(`Ошибка расчета доставки: ${errorData.message || 'Неизвестная ошибка'}`);
    }

    return await safeJsonParse(response);
    */
  } catch (error: any) {
    throw error;
  }
}

// Создать отзыв о заказе
export async function createReview(orderId: string, rating: number, comment: string): Promise<any> {
  try {

    const response = await fetch(`${ORDERS_CONFIG.baseUrl}/orders/${orderId}/review`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify({ rating, comment })
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response);
      throw new Error(`Ошибка создания отзыва: ${errorData.message || 'Неизвестная ошибка'}`);
    }

    return await safeJsonParse(response);
  } catch (error: any) {
    throw error;
  }
}

// Получить отзыв о заказе
export async function getReviewForOrder(orderId: string): Promise<any> {
  try {

    const response = await fetch(`${ORDERS_CONFIG.baseUrl}/orders/${orderId}/review`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      }
    });

    if (!response.ok) {
      return null; // Отзыв не найден
    }

    return await safeJsonParse(response);
  } catch (error: any) {
    return null;
  }
}